import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader, Reveal } from "@/components/bits";
import { COMPANY, SALES_PHONE, TECH_PHONE, WHATSAPP_LINK } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, required, error, children, testId }) => (
  <div>
    <label htmlFor={testId} className="block text-sm font-semibold text-navy">
      {label} {required && <span className="text-brand">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
    {error && (
      <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${testId}-error`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls = (invalid) =>
  `w-full rounded-lg border bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20 ${
    invalid ? "border-red-400" : "border-slate-300"
  }`;

const Contact = () => {
  const location = useLocation();
  const prefill = location.state?.product
    ? `I would like to enquire about ${location.state.product}.`
    : "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    details: prefill,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Please enter a valid email ID.";
    if (!/^\+?[0-9][0-9\s\-]{6,19}$/.test(form.phone.trim()))
      e.phone = "Please enter a valid phone number.";
    if (form.details.trim().length < 10)
      e.details = "Please describe your requirement (at least 10 characters).";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim() || null,
        details: form.details.trim(),
      });
      toast.success("Your enquiry has been submitted. Our team will get back to you shortly.", {
        duration: 5000,
      });
      setForm({ name: "", email: "", phone: "", company: "", details: "" });
      setErrors({});
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit your enquiry right now. Please call us or send a WhatsApp message instead.",
        { duration: 6000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Us | Ambrotech Private Limited"
        description="Contact Ambrotech: sales enquiries on +91 92292 04554, technical support on +91 80764 57992 (call and WhatsApp), email official.ambrotech@gmail.com, or submit an online enquiry."
        path="/contact"
      />

      <PageHeader
        eyebrow="Contact Us"
        title="Talk to Ambrotech"
        lead="Call, WhatsApp, or email us — or send your enquiry through the form below."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          {/* Contact options */}
          <div className="space-y-5">
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="contact-sales-card">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">Sales Enquiry</p>
                <p className="mt-3 font-display text-2xl font-bold text-navy">{SALES_PHONE.label}</p>
                <p className="mt-1 text-sm text-slate-500">For sales enquiries and calls only.</p>
                <a
                  href={SALES_PHONE.tel}
                  data-testid="call-sales-button"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light"
                >
                  <Phone className="h-4 w-4" />
                  Call Sales
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="contact-tech-card">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">Technical Support</p>
                <p className="mt-3 font-display text-2xl font-bold text-navy">{TECH_PHONE.label}</p>
                <p className="mt-1 text-sm text-slate-500">For technical support, on call and WhatsApp.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={TECH_PHONE.tel}
                    data-testid="call-technical-support-button"
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light"
                  >
                    <Phone className="h-4 w-4" />
                    Call Technical Support
                  </a>
                  <a
                    href={TECH_PHONE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-technical-support-button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="contact-email-card">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">Email</p>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="email-link"
                  className="mt-3 inline-flex items-center gap-2 font-display text-lg font-bold text-navy transition-colors hover:text-brand"
                >
                  <Mail className="h-5 w-5 text-brand" />
                  {COMPANY.email}
                </a>
                <p className="mt-2 text-sm text-slate-500">
                  Enquiries submitted through the form are delivered to this inbox.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Enquiry form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={onSubmit}
              noValidate
              className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_12px_36px_rgba(11,25,44,0.06)] sm:p-9"
              data-testid="enquiry-form"
              aria-label="General enquiry form"
            >
              <h2 className="font-display text-xl font-bold text-navy sm:text-2xl">General Enquiry Form</h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Fill in your details and we will respond to your enquiry.
              </p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <Field label="Name" required error={errors.name} testId="enquiry-name-input">
                  <input
                    id="enquiry-name-input"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className={inputCls(!!errors.name)}
                    data-testid="enquiry-name-input"
                  />
                </Field>
                <Field label="Email ID" required error={errors.email} testId="enquiry-email-input">
                  <input
                    id="enquiry-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls(!!errors.email)}
                    data-testid="enquiry-email-input"
                  />
                </Field>
                <Field label="Phone Number" required error={errors.phone} testId="enquiry-phone-input">
                  <input
                    id="enquiry-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 00000 00000"
                    className={inputCls(!!errors.phone)}
                    data-testid="enquiry-phone-input"
                  />
                </Field>
                <Field label="Company Name" testId="enquiry-company-input">
                  <input
                    id="enquiry-company-input"
                    type="text"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Optional"
                    className={inputCls(false)}
                    data-testid="enquiry-company-input"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Enquiry Details" required error={errors.details} testId="enquiry-details-textarea">
                    <textarea
                      id="enquiry-details-textarea"
                      rows={5}
                      value={form.details}
                      onChange={set("details")}
                      placeholder="Please describe your requirements, quantity, technical specifications, project details, or any other information."
                      className={`${inputCls(!!errors.details)} resize-y`}
                      data-testid="enquiry-details-textarea"
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-button"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                    Submitting…
                  </>
                ) : (
                  "Submit Enquiry"
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;